/**
 * 관리자 Layout
 * --
 */
import React, { useEffect, useState } from 'react';
import { Affix, Button, FloatButton, Layout, Menu } from 'antd';
import { AdminHeader, Content } from 'components';
import { useLocation } from 'react-router-dom';
import {
  BarsOutlined,
  DoubleLeftOutlined,
  DoubleRightOutlined,
} from '@ant-design/icons';
import './SideMenu.style.css';
import { useBreadcrumbStack } from 'store/BreadcrumbStore';
import { deleteCookie, DEPLOY_TYPE, DOMAIN_URL, isMobile } from 'utils';
import { useUserAction } from 'store';

const {
  // Header,
  // Footer,
  Sider,
} = Layout;

const AdminLayout = (props) => {
  /* ===== Props ===== */
  const {
    menu,
    children,
    headerExceptPath,
    footerExceptPath,
    siderExceptPath,
    selectedKeys,
    onSelect,
    brand = '',
    defaultSelectedKeys,
    defaultOpenKeys,
  } = props;

  /* ===== Route ===== */
  const location = useLocation();
  const { pathname } = location;
  const mobileFlag = isMobile();

  /* ===== Action ===== */
  const userAction = useUserAction();

  /* ===== State ===== */
  const breadcrumb = useBreadcrumbStack();
  const [isHeader, setIsHeader] = useState(true);
  // const [isFooter, setIsFooter] = useState(true);
  const [isSider, setIsSider] = useState(true);
  // 사이드메뉴 Collapse 키
  const [collapsed, setCollapsed] = useState(false);
  // 사이드메뉴 open 키
  const [isOpened, setIsOpened] = useState(false);
  const [openKeys, setOpenKeys] = useState([]);

  const [isMobileCheck, setIsMobileCheck] = useState(false);
  const [isShow, setIsShow] = useState(false);

  /* ===== Hooks ===== */
  /**
   * 모바일페이지 여부
   * --
   */
  useEffect(() => {
    if (mobileFlag) setCollapsed(true);
  }, [mobileFlag]);

  /**
   * 최초 접속 시(새로고침 포함) 선택된 메뉴가 있을 경우 전처리
   * --
   */
  useEffect(() => {
    if (openKeys.length <= 0 || openKeys[0] === '')
      setOpenKeys(defaultOpenKeys);
  }, [defaultOpenKeys]);
  /**
   * 헤더, 푸터, 사이드메뉴 제외 루트 설정
   * --
   */
  useEffect(() => {
    const call = (path) => {
      const splitPath = path.split('/')[1] ?? '';

      // 헤더 제외 루트 체크
      if (headerExceptPath.includes(splitPath)) {
        setIsHeader(false);
      } else {
        setIsHeader(true);
      }

      // 푸터 제외 루트 체크
      // if (footerExceptPath.includes(splitPath)) {
      //   setIsFooter(false);
      // } else {
      //   setIsFooter(true);
      // }

      // 사이드 메뉴 제외 루트 체크
      if (siderExceptPath.includes(splitPath)) {
        setIsSider(false);
      } else {
        setIsSider(true);
      }
    };
    pathname && call(pathname);
  }, [pathname, headerExceptPath, footerExceptPath, siderExceptPath]);

  /**
   * 전체 메뉴 열기/닫기 설정
   * --
   */
  useEffect(() => {
    if (openKeys?.length === 0) setIsOpened(false);
  }, [openKeys]);

  /* ===== Function ===== */
  const handleOpenMenu = (k) => {
    // alert(k);
    setOpenKeys(k);
  };

  /**
   * 전체 메뉴 열기/닫기
   * --
   */
  const handleOpenToggleMenu = (t) => {
    const type = t.toLocaleUpperCase();

    switch (type) {
      case 'OPEN':
        const keys = menu.reduce((acc, cur) => {
          acc.push(cur.key);

          return acc;
        }, []);
        setOpenKeys(keys);
        setIsOpened(true);
        break;
      case 'CLOSE':
        setOpenKeys([]);
        setIsOpened(false);
        break;
      default:
        break;
    }
  };

  /**
   * 로그아웃
   * --
   */
  const handleLogout = () => {
    const confirm = window.confirm('로그아웃하시겠습니까?');

    if (!confirm) return;

    deleteCookie('JM_COFFEE_ACCESS_TOKEN', {});
    userAction.resetUser();

    const protocol = window.location.protocol;
    const port = DEPLOY_TYPE === 'local' ? ':3000' : '';
    // const domain =
    //   DEPLOY_TYPE === 'development' ? `jm-coffee.${DOMAIN_URL}` : DOMAIN_URL;
    const domain =
      DEPLOY_TYPE === 'development'
        ? `mall-jm-coffee.${DOMAIN_URL}`
        : `mall.${DOMAIN_URL}`;
    return (window.location.href = `${protocol}//${domain}${port}/#/admin`);
  };

  /* ===== Style ===== */
  const styles = {
    layout: {
      height: '100vh',
      position: 'relative',
      top: 0,
      left: 0,
    },
    header: {
      position: 'fixed',
      top: 0,
      left: isSider && !isMobile() ? (collapsed ? 80 : 250) : 0,
      width: `calc(100% - ${
        isSider && !isMobile() ? (collapsed ? '80px' : '250px') : '0px'
      })`,
      zIndex: 1000,
      height: 68,
      background: '#FFFFFF',
      padding: '0px 20px',
    },
    sider: {
      overflow: 'hidden',
      height: `100vh`,
      position: 'fixed',
      left: !isMobileCheck ? 0 : isShow ? 0 : collapsed ? -80 : -250,
      top: 0,
      bottom: 0,
      zIndex: 100,
    },
    container: {
      position: 'absolute',
      top: isHeader ? 68 : 0,
      left: isSider && !isMobile() ? (collapsed ? 80 : 250) : 0,
      // NOTE: 스크린 넓이 - 사이드메뉴 - 스크롤바 넓이
      width:
        isSider && !isMobile()
          ? `calc(100vw - ${collapsed ? '80px' : '250px'})`
          : '100vw',
      background: '#F8F8F8',
    },
    content: {
      // NOTE: 스크린 높이 - 헤더 높이 - 푸터 높이
      // height: `calc(100vh - ${isHeader ? '68px' : '0px'} - ${
      //   isFooter ? '150px' : '0px'
      // })`,
      height: `calc(100vh - ${isHeader ? '68px' : '0px'})`,
      overflow: 'auto',
      position: 'relative',
      top: 0,
      left: 0,
    },
    footer: {
      height: 150,
    },
    siderHeader: {
      padding: '15px 20px',
      background: '#707070',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
    },
  };

  useEffect(() => {
    setIsMobileCheck(isMobile());
  }, []);

  /* ===== Render ===== */
  return (
    <Layout style={styles.layout}>
      {/* 사이드 메뉴 */}
      {isSider && (isMobileCheck ? isShow : true) && (
        <>
          <Sider
            style={styles.sider}
            trigger={null}
            collapsible
            collapsed={collapsed}
            width={250}
          >
            <div
              className="logo"
              style={{
                ...styles.siderHeader,
                paddingBottom: 20,
                // background: '#636262',
              }}
            >
              {!collapsed && (
                <h4
                  style={{
                    fontSize: 16,
                    fontWeight: 500,
                    color: '#FFFFFF',
                  }}
                >
                  {/* JMCOFFEE */}
                  <img
                    src="/static/media/logo_w.e9cd5033688d4a828622df2ab1a8420d.svg"
                    alt=""
                    width={125}
                  />{' '}
                  {/* <span>ADMIN</span> */}
                </h4>
              )}
              <Button
                icon={
                  collapsed ? <DoubleRightOutlined /> : <DoubleLeftOutlined />
                }
                type="link"
                onClick={() => setCollapsed(!collapsed)}
                style={{ color: '#FFFFFF' }}
              />
            </div>

            <Menu
              items={menu}
              mode="inline"
              // openKeys={openKeys}
              // selectedKeys={selectedKeys}
              style={{
                height: 'calc(100% - 64px)',
                overflow: 'auto',
                background: '#707070',
              }}
              // 상위항목 선택 이벤트
              onOpenChange={(key) => {
                console.log(key);
                handleOpenMenu(key);
              }}
              // 하위항목 선택 이벤트
              onSelect={({ key }) => {
                onSelect(key);
              }}
              openKeys={openKeys} // 상위항목 펼치기
              defaultOpenKeys={defaultOpenKeys}
              defaultSelectedKeys={defaultSelectedKeys}
            />
          </Sider>
        </>
      )}
      {/* 사이드 메뉴 끝 */}

      {/* 사이드 메뉴 / 본문 / 푸터 */}
      <Layout style={{ background: 'none' }}>
        {/* 해더 */}
        {isHeader && (
          <AdminHeader
            maxWidth={'100%'}
            style={styles.header}
            name={'jmcoffee'}
            leftContent={breadcrumb}
            rightContent={[
              ...(mobileFlag
                ? []
                : [
                    {
                      title: isOpened ? '전체메뉴 닫기' : '전체메뉴 열기',
                      onClick: () =>
                        handleOpenToggleMenu(isOpened ? 'close' : 'open'),
                    },
                    // { title: '공지사항' },
                    // { title: '매뉴얼' },
                  ]),
              { title: <span style={{ fontWeight: 600 }}>{brand}</span> },
            ]}
            onLogout={handleLogout}
          />
        )}
        {/* 해더 끝 */}

        {/* 본문 / 푸터 */}
        <Layout style={styles.container}>
          {/* 본문 */}
          <Content
            maxWidth={'100%'}
            backgroundColor={'#F7F7F8'}
            padding={0}
            style={styles.content}
          >
            {children}
          </Content>
          {/* 본문 끝 */}

          {/* 푸터 */}
          {/* {isFooter && <Footer style={styles.footer}>푸터</Footer>} */}
          {/* 푸터 끝 */}
        </Layout>
        {/* 본문 / 푸터 끝 */}

        {isMobileCheck ? (
          <FloatButton
            onClick={() => setIsShow(!isShow)}
            type="primary"
            icon={<BarsOutlined />}
          >
            zz
          </FloatButton>
        ) : null}
      </Layout>
    </Layout>
  );
};

export default AdminLayout;
